import { useSeoMeta, useHead } from '@unhead/react';
import {ArrowRight, CheckCircle2, Clock3, Shield, Star, Truck} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { SiteShell } from '@/components/SiteShell';
import { normalizeCanonicalUrl } from '@/lib/seo';
import defaultServiceBg from '../../images/VIP House Movers Removal Van.jpg';

function getServiceSearchTerms(title: string): string[] {
  const areaMatch = title.match(/in\s+(.+)$/i);
  const area = areaMatch?.[1] ?? '';
  const serviceLabel = title.replace(/\s+in\s+.+$/i, '').trim();
  const normalizedService = serviceLabel.toLowerCase();

  return [
    title,
    `${serviceLabel} ${area}`.trim(),
    `${normalizedService} ${area}`.trim(),
    `${normalizedService} near me`,
    `local ${normalizedService} company`,
    `${normalizedService} West Midlands`,
    `removals ${area}`.trim(),
    `moving services ${area}`.trim(),
  ].filter(Boolean);
}


type ServiceDetailPageProps = {
  activePath: string;
  serviceMenuPath?: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
  seoPoints?: string[];
  faqs?: Array<{ question: string; answer: string }>;
  backgroundImage?: string;
};

export function ServiceDetailPage({
  activePath,
  serviceMenuPath,
  title,
  description,
  intro,
  highlights,
  process,
  idealFor,
  seoPoints = [],
  faqs = [],
  backgroundImage,
}: ServiceDetailPageProps) {
  const searchTerms = getServiceSearchTerms(title);

  useSeoMeta({
    title: `${title} | VIP House Movers`,
    description: description.slice(0, 155),
    ogTitle: `${title} | VIP House Movers`,
    ogDescription: description.slice(0, 155),
    ogImage: backgroundImage ?? 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: `https://www.viphousemovers.com${activePath}`,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: `${title} | VIP House Movers`,
    twitterDescription: description.slice(0, 155),
    twitterImage: backgroundImage ?? 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl(`https://www.viphousemovers.com${activePath}`);

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: `${title}, ${searchTerms.join(', ')}, home removals, house removals, moving services, removals service, man and van, packing services, storage solutions, professional movers, trusted removals company, local movers`,
      },
    ],
  });
  return (
    <SiteShell activePath={activePath} fixedNav serviceMenuPath={serviceMenuPath ?? activePath}>
      <PageHero
        eyebrow="VIP House Movers Services"
        title={title}
        description={description}
        primaryLabel="Get a Free Quote"
        primaryTo="/quote"
        secondaryLabel="Contact VIP House Movers"
        secondaryTo="/contact"
        backgroundImage={backgroundImage ?? defaultServiceBg}
      />

      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.3fr_0.7fr] items-start">
            <div className="space-y-8">
              <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                  <Star className="h-4 w-4" />
                  Premium Service
                </div>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">Tailored support for every move</h2>
                <p className="mt-5 text-lg leading-relaxed text-gray-600">
                  {intro}
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {highlights.map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37]/10">
                      <CheckCircle2 className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <p className="text-base font-medium leading-7 text-gray-700">{item}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                  Search-friendly phrases
                </div>
                <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">What customers are searching for</h2>
                <p className="mt-5 text-base leading-7 text-gray-600">
                  These key search phrases match how people look for moving support in your area, helping this page rank for high-value removals queries.
                </p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {searchTerms.map((term) => (
                    <div key={term} className="rounded-2xl border border-gray-200 bg-gray-50 p-4 text-gray-700">
                      {term}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl bg-gray-900 p-8 text-white shadow-xl">
                <h2 className="text-3xl font-bold tracking-tight">How VIP House Movers delivers this service</h2>
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  {process.map((step, index) => (
                    <div key={step} className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                      <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AF37] text-sm font-bold text-gray-900">
                        {index + 1}
                      </div>
                      <p className="text-base leading-7 text-gray-200">{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {seoPoints.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-4 py-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#B78D19]">
                    Search-Friendly Highlights
                  </div>
                  <h2 className="mt-5 text-3xl font-bold tracking-tight text-gray-900">What makes this VIP House Movers service stand out</h2>
                  <div className="mt-8 grid gap-4 md:grid-cols-2">
                    {seoPoints.map((point) => (
                      <div key={point} className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                        <div className="flex items-start gap-3 text-gray-700">
                          <CheckCircle2 className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
                          <span className="leading-7">{point}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {faqs.length > 0 ? (
                <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                  <h2 className="text-3xl font-bold tracking-tight text-gray-900">Frequently asked questions</h2>
                  <div className="mt-8 space-y-4">
                    {faqs.map((faq) => (
                      <details key={faq.question} className="group rounded-2xl border border-gray-200 bg-gray-50 p-5 hover:border-[#D4AF37]/40 transition-colors">
                        <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-lg font-semibold text-gray-900">
                          <span>{faq.question}</span>
                          <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37] transition-transform group-open:rotate-90" />
                        </summary>
                        <p className="mt-4 leading-7 text-gray-600">{faq.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="space-y-6 lg:sticky lg:top-32">
              <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm">
                <h3 className="text-2xl font-bold text-gray-900">Best suited for</h3>
                <ul className="mt-6 space-y-4">
                  {idealFor.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-gray-600">
                      <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 text-[#D4AF37]" />
                      <span className="leading-7">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-8 shadow-sm">
                <div className="flex items-center gap-3 text-gray-900">
                  <Shield className="h-6 w-6 text-[#D4AF37]" />
                  <h3 className="text-xl font-bold">Why choose VIP House Movers?</h3>
                </div>
                <div className="mt-6 space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <Truck className="h-5 w-5 text-[#D4AF37]" />
                    <span>Professional movers with premium care standards</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock3 className="h-5 w-5 text-[#D4AF37]" />
                    <span>Prompt communication and reliable arrival times</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#D4AF37]" />
                    <span>Careful handling for homes, offices, and valuable items</span>
                  </div>
                </div>
                <div className="mt-8 flex flex-col gap-3">
                  <Link
                    to="/quote"
                    className="inline-flex items-center justify-center rounded-xl bg-[#D4AF37] px-6 py-3 text-base font-bold text-gray-900 transition-all duration-300 hover:scale-[1.02] hover:bg-[#c79f2e]"
                  >
                    Request Your Quote
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-6 py-3 text-base font-bold text-gray-800 transition-colors hover:border-[#D4AF37] hover:text-[#B78D19]"
                  >
                    View All Services
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

